import { MONTH_LABELS } from '@crm/shared';
import { Segmented, Select } from '@crm/ui';
import type { LucideIcon } from 'lucide-react';
import { CalendarDays, CalendarRange, Layers, Megaphone, Sprout, Target } from 'lucide-react';
import { Autocomplete } from '@/components';
import { useCampaigns } from '@/modules/campaigns/api';

export type ConsultaModo = 'geral' | 'dias45' | 'trimestre' | 'mes' | 'ano' | 'campanha';

/** De onde o lead veio: sozinho, puxado por um disparo, ou tanto faz. */
export type Gatilho = 'all' | 'organic' | 'campaign';

export interface Consulta {
  modo: ConsultaModo;
  /** Recorte do calendario - so o que o modo escolhido usa vale. */
  ano: number;
  trimestre: 1 | 2 | 3 | 4;
  mes: number;
  /** Campanhas escolhidas no modo campanha - vazio quer dizer todas. */
  campaignIds: string[];
  gatilho: Gatilho;
}

const MODOS = [
  { value: 'dias45', label: 'Ultimos 45 dias', icon: CalendarDays },
  { value: 'mes', label: 'Por mes', icon: CalendarRange },
  { value: 'trimestre', label: 'Por trimestre', icon: CalendarRange },
  { value: 'ano', label: 'Por ano', icon: CalendarRange },
  { value: 'campanha', label: 'Por campanha', icon: Megaphone },
  { value: 'geral', label: 'Geral', icon: Layers },
] as const satisfies readonly { value: ConsultaModo; label: string; icon: unknown }[];

const TRIMESTRES = [1, 2, 3, 4] as const;

const GATILHOS = [
  { value: 'all', label: 'Todos os gatilhos', icon: Target },
  { value: 'organic', label: 'Lead organico', icon: Sprout },
  { value: 'campaign', label: 'Campanha de marketing', icon: Megaphone },
] as const satisfies readonly { value: Gatilho; label: string; icon: LucideIcon }[];

const CLASSE_SELECT = 'h-9 w-auto';

/** Os anos que valem escolher: o corrente e os quatro anteriores. */
function anosDisponiveis(ano: number) {
  return Array.from({ length: 5 }, (_, index) => ano - index);
}

/**
 * O recorte que vale para os dois graficos do box.
 *
 * O modo fica em cima e o que ele pede logo abaixo: a pergunta e depois a
 * resposta, na ordem em que se le. Sem rotulo escrito - o proprio modo ja
 * disse o que se esta escolhendo.
 */
export function FunnelFilters({
  consulta,
  onChange,
}: {
  consulta: Consulta;
  onChange: (consulta: Consulta) => void;
}) {
  const campaigns = useCampaigns();
  const opcoes = (campaigns.data?.data ?? []).map((campaign) => ({
    id: campaign.id,
    label: campaign.name,
  }));

  const escolhidas = opcoes.filter((opcao) => consulta.campaignIds.includes(opcao.id));
  const anos = anosDisponiveis(new Date().getFullYear());
  const gatilhoEscolhido = GATILHOS.find((item) => item.value === consulta.gatilho) ?? GATILHOS[0];
  const IconeDoGatilho = gatilhoEscolhido.icon;

  const seletorDeAno = (
    <Select
      aria-label="Ano da consulta"
      className={CLASSE_SELECT}
      value={String(consulta.ano)}
      onChange={(event) => onChange({ ...consulta, ano: Number(event.target.value) })}
    >
      {anos.map((ano) => (
        <option key={ano} value={ano}>
          {ano}
        </option>
      ))}
    </Select>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Segmented
          flat
          label="Filtro de consulta"
          options={MODOS}
          value={consulta.modo}
          onChange={(modo) => onChange({ ...consulta, modo })}
        />

        {/* De onde veio quem entrou - vale para os dois graficos. Select, e
            nao mais um grupo de pilulas: duas fileiras iguais lado a lado se
            leem como um controle so. */}
        {/* Sem moldura e do tamanho do texto: e um rotulo que se troca, nao
            um campo de formulario. A seta encosta no fim da palavra, e o
            icone da opcao escolhida fica a esquerda dela. */}
        {/* Icone e rotulo na mesma linha de base. O select nativo se mede
            pela maior opcao - tentar encolhe-lo ate o texto escolhido
            desalinhava a seta e cortava o rotulo. */}
        <span className="flex items-center gap-1.5 text-primary">
          <IconeDoGatilho className="h-4 w-4 shrink-0" aria-hidden />
          <Select
            aria-label="Gatilho do lead"
            className="h-auto border-0 bg-transparent px-0 py-0 text-sm font-medium leading-none text-primary shadow-none focus-visible:ring-0"
            // O select nativo se mede pela maior opcao. A largura em `ch`
            // acompanha o rotulo escolhido, com folga para a seta.
            style={{ width: `calc(${gatilhoEscolhido.label.length}ch + 1.6rem)` }}
            value={consulta.gatilho}
            onChange={(event) => onChange({ ...consulta, gatilho: event.target.value as Gatilho })}
          >
            {GATILHOS.map((gatilho) => (
              <option key={gatilho.value} value={gatilho.value}>
                {gatilho.label}
              </option>
            ))}
          </Select>
        </span>
      </div>

      {consulta.modo === 'trimestre' ? (
        <div className="flex items-center gap-2">
          <Select
            aria-label="Trimestre da consulta"
            className={CLASSE_SELECT}
            value={String(consulta.trimestre)}
            onChange={(event) =>
              onChange({ ...consulta, trimestre: Number(event.target.value) as 1 | 2 | 3 | 4 })
            }
          >
            {TRIMESTRES.map((trimestre) => (
              <option key={trimestre} value={trimestre}>
                {trimestre}o trimestre
              </option>
            ))}
          </Select>
          {seletorDeAno}
        </div>
      ) : null}

      {consulta.modo === 'mes' ? (
        <div className="flex items-center gap-2">
          <Select
            aria-label="Mes da consulta"
            className={CLASSE_SELECT}
            value={String(consulta.mes)}
            onChange={(event) => onChange({ ...consulta, mes: Number(event.target.value) })}
          >
            {MONTH_LABELS.map((label, indice) => (
              <option key={label} value={indice + 1}>
                {label}
              </option>
            ))}
          </Select>
          {seletorDeAno}
        </div>
      ) : null}

      {consulta.modo === 'ano' ? seletorDeAno : null}

      {consulta.modo === 'campanha' ? (
        <Autocomplete
          multiple
          size="sm"
          variant="filter"
          className="w-full sm:w-72"
          placeholder="Buscar campanha"
          emptyLabel="Todas as campanhas"
          options={opcoes}
          value={escolhidas}
          onChange={(itens) => onChange({ ...consulta, campaignIds: itens.map((item) => item.id) })}
        />
      ) : null}
    </div>
  );
}

/** O comeco e o fim de um dia, em ISO - o filtro do funil fala nessa moeda. */
function janela(de: Date, ate: Date) {
  return {
    from: new Date(de.getFullYear(), de.getMonth(), de.getDate()).toISOString(),
    to: new Date(ate.getFullYear(), ate.getMonth(), ate.getDate(), 23, 59, 59).toISOString(),
  };
}

export interface RecorteDaConsulta {
  from?: string;
  to?: string;
  campaignIds?: string;
}

/** O que a consulta vira na chamada da API - vazio quando o modo nao recorta. */
export function paramsDaConsulta(consulta: Consulta): RecorteDaConsulta {
  if (consulta.modo === 'campanha') {
    return { campaignIds: consulta.campaignIds.join(',') || undefined };
  }

  if (consulta.modo === 'dias45') {
    const hoje = new Date();

    return janela(new Date(hoje.getTime() - 45 * 24 * 60 * 60_000), hoje);
  }

  if (consulta.modo === 'trimestre') {
    const primeiroMes = (consulta.trimestre - 1) * 3;

    return janela(
      new Date(consulta.ano, primeiroMes, 1),
      new Date(consulta.ano, primeiroMes + 3, 0),
    );
  }

  if (consulta.modo === 'mes') {
    return janela(
      new Date(consulta.ano, consulta.mes - 1, 1),
      new Date(consulta.ano, consulta.mes, 0),
    );
  }

  if (consulta.modo === 'ano') {
    return janela(new Date(consulta.ano, 0, 1), new Date(consulta.ano, 11, 31));
  }

  return {};
}
